import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Alert, Badge, Button, Card, CardBody, Col, Container, Media, Row, Spinner, Table } from "reactstrap"
import { get } from "helpers/api_helper"
import Breadcrumbs from "components/Common/Breadcrumb"
import { courierAlertNames, courierStatusLabel, dayLabel, shipmentTitle } from "pages/EventsManagement/components/Tabs/Logistics/shipmentLabels"
import { sheetInProcessing, sheetReadyToShip, sheetShipped } from "pages/EventsManagement/components/Tabs/Logistics/sheetState"
import { datesLabel } from "../Sheets/sheetLabels"
import { packagesLabel, placedName, productName } from "../Stock/stockForm"
import { amountLabel, canceledAlertDetail, canceledAlertName, emailAlertDetail, emailAlertName, emailFailed, horizonEnd, leftLabel, prepareDetail, prepareIcon,
  prepareTag, reasonColor, reasonDetail, reasonMark, reasonStatus, reasonsAt, shippedLabel } from "./warehouseLabels"
import Forecast from "./Forecast"
import "pages/EventsManagement/components/Tabs/Logistics/logistics.scss"
import "./warehouse.scss"

// One key figure: its label, which opens where it is detailed, its value and its icon.
const Figure = ({ label, to, value, icon, color }) => <Col sm={6} xl={3}>
  <Card className="mini-stats-wid"><CardBody><Media>
    <Media body>
      <p className="font-weight-medium mb-1">{to.startsWith("#") ? <a href={to} className="text-muted">{label}</a>
        : <Link to={to} className="text-muted">{label}</Link>}</p>
      <h4 className="mb-0">{value}</h4>
    </Media>
    <div className="avatar-sm align-self-center">
      <span className={`avatar-title rounded-circle bg-soft-${color} text-${color}`}><i className={`bx ${icon} font-size-24`} aria-hidden="true" /></span>
    </div>
  </Media></CardBody></Card>
</Col>

// The event a row is about, opening its Logistics tab, with its days.
const EventTitle = ({ event }) => <div><Link to={`/events/${event.id}/logistics`}><strong>{event.name || "Unnamed event"}</strong></Link>
  <span className="text-muted">{` · ${datesLabel(event.startDate, event.endDate)}`}</span></div>

// A shipment email that failed or is unconfirmed (US 170): its event, opening the Logistics tab where it is resent, and whom it did not reach.
const EmailAlert = ({ alert }) => {
  const color = emailFailed(alert) ? "danger" : "warning"
  return <li>
    <i className={`bx bx-envelope text-${color} font-size-18`} aria-hidden="true" />
    <div className="logistics-warehouse-item">
      <EventTitle event={alert.event} />
      <div><Badge color={`soft-${color}`}>{emailAlertName(alert)}</Badge></div>
      <div className="small text-muted">{emailAlertDetail(alert)}</div>
    </div>
  </li>
}

// A Cargus shipment the courier has not delivered the day before its event, or returned to the sender until its return is recorded (US 110):
// its event, opening the Logistics tab where the return is recorded, and the courier's last status.
const CourierAlert = ({ alert }) => <li>
  <i className="bx bx-package text-danger font-size-18" aria-hidden="true" />
  <div className="logistics-warehouse-item">
    <EventTitle event={alert.event} />
    <div>{alert.alerts.map(kind => <Badge key={kind} color="soft-danger" className="mr-1">{courierAlertNames[kind]}</Badge>)}</div>
    <div className="small text-muted">{[shipmentTitle(alert.shipment), `AWB ${alert.shipment.awb}`, courierStatusLabel(alert.status)].join(" · ")}</div>
  </div>
</li>

// A canceled event (D10; Tudor, 2026-10-23): its goods that left and did not come back, until their return or the signal's closing, or the AWB
// a shipment that did not leave still holds, until it is cancelled; its event opens the Logistics tab where either is done.
const CanceledAlert = ({ alert }) => <li>
  <i className="bx bx-calendar-x text-danger font-size-18" aria-hidden="true" />
  <div className="logistics-warehouse-item">
    <EventTitle event={alert.event} />
    <div><Badge color="soft-danger">{canceledAlertName(alert)}</Badge></div>
    <div className="small text-muted">{canceledAlertDetail(alert)}</div>
  </div>
</li>

// The shipment emails that failed, each until a resend is sent, the courier's alerts and the canceled events', then one row per product, one
// mark per reason, the urgent ones first (tickets 20 Q6, 22 Q2 and 27 Q11): only here and on the product, never by email. The stock alerts
// once the initial inventory started it.
const Alerts = ({ alerts, emailAlerts, courierAlerts, canceledAlerts, total, started }) => <Card tag="section" aria-label="Alerts" id="warehouse-alerts"><CardBody>
  <h5 className="card-title">Alerts <Badge color="soft-danger">{total}</Badge></h5>
  <p className="text-muted small">One row per shipment email that failed, until it is resent, one per Cargus shipment the courier has not delivered before
    its event or returned to the sender, one per canceled event whose goods left or whose AWB is still active, and one per product, one mark per
    reason. Shown only here and on the product, never sent by email.</p>
  {total > 0 && <ul className="list-unstyled mb-0 logistics-warehouse-list">
    {emailAlerts.map(alert => <EmailAlert key={`${alert.event.id}-${alert.shipment.kind}-${alert.shipment.id || ""}`} alert={alert} />)}
    {courierAlerts.map(alert => <CourierAlert key={`courier-${alert.shipment.id}`} alert={alert} />)}
    {canceledAlerts.map(alert => <CanceledAlert key={`${alert.kind}-${alert.event.id}`} alert={alert} />)}
    {alerts.map(alert => <li key={alert.product.id}>
      <i className={`bx ${alert.severity === "danger" ? "bx-error text-danger" : "bx-error-circle text-warning"} font-size-18`} aria-hidden="true" />
      <div className="logistics-warehouse-item">
        <strong>{productName(alert.product)}</strong>
        <div>{alert.reasons.map(reason => <Badge key={`${reason.kind}-${reason.place}`} color={reasonColor(reason)} className="mr-1">{reasonMark(reason)}</Badge>)}</div>
        <div className="small text-muted">{alert.reasons.map(reason => reasonDetail(reason, alert.product)).join(" · ")}</div>
      </div>
    </li>)}
  </ul>}
  {!started ? <p className="text-muted mb-0">No balance to alert on before the initial inventory.</p>
    : !total && <p className="text-muted mb-0">No alerts.</p>}
</CardBody></Card>

const sheetBadge = sheet => {
  if (sheetShipped(sheet.status)) {
    return <><Badge color="soft-success">Sheet shipped</Badge>{sheet.mainShipment && <>{" "}<span className="small text-muted">main {dayLabel(sheet.mainShipment)}</span></>}</>
  }
  if (sheetReadyToShip(sheet.status)) return <Badge color="soft-primary">Sheet ready to be shipped</Badge>
  return sheetInProcessing(sheet.status) ? <Badge color="soft-info">Sheet ready for processing</Badge> : <Badge color="soft-warning">Sheet in progress</Badge>
}

// What is to prepare, the nearest event first, each with its sheet's state (ticket 22, Q3), then the near events already shipped.
const ToPrepare = ({ rows, shipped, now }) => <Card tag="section" aria-label="To prepare" id="warehouse-to-prepare"><CardBody>
  <div className="logistics-warehouse-head">
    <h5 className="card-title">To prepare <Badge color="soft-warning">{rows.length}</Badge></h5>
    <Link to="/logistics/sheets" className="small">All sheets</Link>
  </div>
  <p className="text-muted small">Actions on sheets and shipments, nearest event first. Main shipments are listed from 14 days before the event.</p>
  {rows.length ? <ul className="list-unstyled mb-0 logistics-warehouse-list">{rows.map(row => {
    const tag = prepareTag(row, now)
    return <li key={`${row.event.id}-${row.kind}-${row.shipment?.id || ""}`}>
      <i className={`bx ${prepareIcon(row)} text-muted font-size-18`} aria-hidden="true" />
      <div className="logistics-warehouse-item">
        <EventTitle event={row.event} />
        <div className="small text-muted">{prepareDetail(row)}</div>
        <div className="mt-1">{sheetBadge(row.sheet)}</div>
      </div>
      <Badge color={tag.color} className="align-self-start">{tag.label}</Badge>
    </li>
  })}</ul> : <p className="text-muted mb-0">Nothing to prepare.</p>}
  {shipped.length > 0 && <>
    <h6 className="text-muted small text-uppercase mt-3">Already shipped · events in the next 14 days</h6>
    <ul className="list-unstyled mb-0 logistics-warehouse-list" aria-label="Already shipped · events in the next 14 days">{shipped.map(({ event, shipment }) =>
      <li key={event.id}>
        <i className="bx bx-check-circle text-success font-size-18" aria-hidden="true" />
        <div className="logistics-warehouse-item">
          <EventTitle event={event} />
          <div className="small text-muted">{shippedLabel(shipment)}</div>
          <div className="mt-1"><Badge color="soft-success">Sheet shipped</Badge></div>
        </div>
      </li>)}</ul>
  </>}
</CardBody></Card>

// Each product's balance per place, the pending need of the next 30 and 60 days and what each leaves, with the product's marks.
const StockAndNeed = ({ stock, alerts, started }) => <Card tag="section" aria-label="Stock and pending need"><CardBody>
  <h5 className="card-title">Stock and pending need</h5>
  <p className="text-muted small">Pending need = effective quantities of the unshipped sheets now, plus supplementary shipments in preparation, for the events
    that start in the next 30 or 60 days; one that has started and not ended counts too. Disabled or deleted events and competitions are left out.</p>
  <Table size="sm" className="logistics-stack mb-0">
    <thead><tr>
      <th>Product</th><th className="text-right">Balance</th><th className="text-right">Next 30 days</th><th className="text-right">Next 60 days</th><th>Status</th>
    </tr></thead>
    <tbody>{stock.flatMap(({ product, balances }) => balances.map(({ place, balance, need }) => {
      const reasons = reasonsAt(alerts.find(alert => alert.product.id === product.id), place)
      return <tr key={`${product.id}-${place}`}>
        <td><strong>{placedName(product.name, place)}</strong>{!product.active && <Badge color="soft-secondary" className="ml-1">Inactive</Badge>}
          {product.minimum !== null && <div className="small text-muted">minimum {product.minimum}</div>}</td>
        <td className="text-right" data-label="Balance">{balance === null ? "—" : <>
          <span className={balance < 0 ? "text-danger font-weight-medium" : ""}>{amountLabel(product, balance)}</span>
          {packagesLabel(product, balance) && <div className="small text-muted">{packagesLabel(product, balance)}</div>}
        </>}</td>
        {need.map(({ days, quantity, left }) => <td key={days} className="text-right" data-label={`Next ${days} days`}>{quantity || "—"}
          {left !== null && <div className={`small ${left < 0 ? "text-danger" : "text-muted"}`}>{leftLabel(left)}</div>}</td>)}
        <td data-label="Status">{!started ? "—" : reasons.length ? reasons.map(reason => <Badge key={`${reason.kind}-${reason.place}`}
          color={reasonColor(reason)} className="mr-1">{reasonStatus(reason)}</Badge>) : <Badge color="soft-success">OK</Badge>}</td>
      </tr>
    }))}</tbody>
  </Table>
</CardBody></Card>

// Logistics → Warehouse (US 122–130, tickets 17, 22 and 29, layout A): the key figures, the alerts beside what is to prepare, the stock
// with its pending need, then the consumption forecast of the next 6 months.
export function LogisticsWarehouse({ now = () => new Date() }) {
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState({ loading: true })

  useEffect(() => {
    let current = true
    setState({ loading: true })
    get("/admin/logistics/warehouse")
      .then(response => current && setState({ answer: response?.data }))
      .catch(() => current && setState({ error: "The warehouse could not be loaded." }))
    return () => { current = false }
  }, [attempt])

  const { answer } = state
  const started = Boolean(answer?.startedAt)
  const emailAlerts = answer?.emailAlerts || []
  const courierAlerts = answer?.courierAlerts || []
  const canceledAlerts = answer?.canceledAlerts || []
  // Every alert the page lists: the emails', the courier's, the canceled events' and the products'.
  const alertTotal = emailAlerts.length + courierAlerts.length + canceledAlerts.length + (answer?.alerts.length ?? 0)
  return <div className="page-content logistics-warehouse">
    <Container fluid>
      <Breadcrumbs title="Logistics" breadcrumbItem="Warehouse" path="/logistics/warehouse" />
      {state.error && <Alert color="danger">{state.error}{" "}
        <Button color="link" className="p-0 align-baseline" onClick={() => setAttempt(value => value + 1)}>Retry</Button>
      </Alert>}
      {state.loading && <div className="text-center my-4"><Spinner color="primary" /></div>}
      {answer && <>
        {!started && <Alert color="info">The stock starts with the initial inventory, recorded on the <Link to="/logistics/stock">Stock</Link> page.
          Until then there is no balance: the page shows the pending need, and no stock alert.</Alert>}
        <section aria-label="Key figures"><Row>
          <Figure label="Alerts" to="#warehouse-alerts" value={alertTotal} icon="bx-bell" color="danger" />
          <Figure label="To prepare" to="#warehouse-to-prepare" value={answer.toPrepare.length} icon="bx-task" color="warning" />
          <Figure label="Sheets in progress · 60 days" to={`/logistics/sheets?status=inPreparation&to=${horizonEnd(now)}`}
            value={answer.figures.sheetsInProgress} icon="bx-spreadsheet" color="primary" />
          <Figure label="Shipments on the way" to="/logistics/shipments" value={answer.figures.onTheWay} icon="bx-package" color="success" />
        </Row></section>
        <Row>
          <Col xl={7}><Alerts alerts={answer.alerts} emailAlerts={emailAlerts} courierAlerts={courierAlerts} canceledAlerts={canceledAlerts}
            total={alertTotal} started={started} /></Col>
          <Col xl={5}><ToPrepare rows={answer.toPrepare} shipped={answer.alreadyShipped} now={now} /></Col>
        </Row>
        <StockAndNeed stock={answer.stock} alerts={answer.alerts} started={started} />
        {answer.forecast && <Forecast forecast={answer.forecast} />}
      </>}
    </Container>
  </div>
}

export default LogisticsWarehouse
